import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  classnames,
  Button,
  Check,
  EditOutline,
  Icon,
  RotatingSpinner,
  TextInput,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import CreatorAvatar from 'Pods/Shared/CreatorAvatar/CreatorAvatar'
import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'
import useApiPatch from 'Pods/Shared/Hooks/useApiPatch'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useSentry from 'Pods/Shared/Hooks/useSentry'
import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { patchSessionTitleInLibraryCache } from 'Pods/ThreeDStudio/Shared/Utils/patchSessionTitleInLibraryCache'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setOpenedModelTitle } from 'Pods/ThreeDStudio/Store/actions/ui'

import useIsTextTruncated from './Hooks/useIsTextTruncated'

function ModelTitle() {
  const t = useTranslation()
  const queryClient = useQueryClient()
  const title = useThreeDStudioStore(state => state.ui?.openedModel.title ?? state.ui?.openedModel.prompt)
  const creator = useThreeDStudioStore(state => state.ui?.openedModel?.user)
  const generatorSessionId = useThreeDStudioStore(state => state.generation?.generatorSessionId)
  const { patchData, isLoading } = useApiPatch()
  const { captureException } = useSentry()
  const isReadOnly = useOpenedModelReadOnly()
  // Default options: FooterActions mounts only once a model is opened, after
  // /load-services.js already fired its config event, so read the already-present
  // window.pageConfig on mount rather than waiting for an event that will not
  // fire again (see useOpenedModelReadOnly for the same reasoning).
  const pageConfig = usePageConfig()
  const showCreatorAvatar = !!pageConfig?.aiCreatorAvatarEnabled
  const [isEditing, setIsEditing] = useState(false)
  const [draftTitle, setDraftTitle] = useState(title ?? '')
  const [titleRef, isTitleTruncated] = useIsTextTruncated(title)

  useEffect(() => {
    if (!isEditing) {
      setDraftTitle(title ?? '')
    }
  }, [isEditing, title])

  useEffect(() => {
    setIsEditing(false)
  }, [generatorSessionId])

  const handleStartEditing = useCallback(() => {
    setIsEditing(true)
  }, [])

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleChange = useCallback((e) => {
    setDraftTitle(e.target.value)
  }, [])

  const isSaveDisabled = useMemo(() => (
    !draftTitle
    || draftTitle.trim() === ''
    || (title && draftTitle.trim() === title.trim())
  ), [draftTitle, title])

  const handleConfirm = useCallback(() => {
    if (isLoading) return

    if (isSaveDisabled) {
      handleCancelEditing()
      return
    }

    const nextTitle = draftTitle.trim()

    patchData({
      path: `/api/internal/generator/sessions/${generatorSessionId}`,
      data: { data: { title: nextTitle } },
      onSuccess: () => {
        if (typeof window !== 'undefined' && window.showNotification) {
          window.showNotification(t('footerActions.modelTitle.updateSuccess'), { type: 'success', autohide: true })
        }
        setOpenedModelTitle(nextTitle)
        patchSessionTitleInLibraryCache(queryClient, {
          sessionId: generatorSessionId,
          title: nextTitle,
        })
        setIsEditing(false)
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.errors?.[0] || t('footerActions.modelTitle.updateError')
        captureException(error)
        if (typeof window !== 'undefined' && window.showNotification) {
          window.showNotification(errorMessage, { type: 'error', autohide: true })
        }
        setIsEditing(false)
      },
    })
  }, [
    captureException,
    draftTitle,
    generatorSessionId,
    handleCancelEditing,
    isLoading,
    isSaveDisabled,
    patchData,
    queryClient,
    t,
  ])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEditing()
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirm()
    }
  }, [handleCancelEditing, handleConfirm])

  const confirmIcon = isLoading
    ? <Icon svgComponent={RotatingSpinner} color="dark" size="small" notSpaced disabled />
    : <Icon svgComponent={Check} color="dark" size="small" notSpaced />

  const creatorTooltip = creator?.username
    ? t('footerActions.creator.createdBy', { username: creator.username })
    : null

  // `--empty` opts the placeholder out of the grow/min-width sizing below: an
  // opened model can carry neither a title nor a prompt, and reserving the wrap
  // threshold for an empty box would push the actions onto a second row.
  if (!title) return <div className="model-title model-title--empty" aria-hidden="true" />

  return (
    <div className={classnames('model-title', isEditing && 'model-title--editing')}>
      {/* Flag-gated (:enable_ai_creator_avatar); only ever shown on someone else's
          model (`isReadOnly`). */}
      {showCreatorAvatar && isReadOnly && (
        <CreatorAvatar
          user={creator}
          tooltipContent={creatorTooltip}
          id="footer-actions-creator-avatar"
          place="top"
          containerClass="creator-avatar"
          className="creator-avatar__image"
        />
      )}
      {isEditing ? (
        <>
          <TextInput
            id="model-title"
            value={draftTitle}
            size="small"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            isDisabled={isLoading}
            isSquishable
            wrapperClassName="model-title__input"
            isPilot
          />
          <Button
            customClass="model-title__edit-button"
            type="secondary"
            size="tiny"
            variant="only-icon"
            aria-label="Confirm model title"
            onClick={handleConfirm}
            disabled={isLoading}
            icon={confirmIcon}
          />
        </>
      ) : (
        <>
          {/* `focusable` only while truncated: it adds a tab stop so keyboard users
              can reveal the hidden part of the name, and there is nothing hidden to
              reveal once the title fits. */}
          <DesktopOnlyTooltip
            id="footer-actions-model-title"
            content={isTitleTruncated ? title : null}
            place="top"
            containerClass="model-title__tooltip"
            className="model-title__tooltip-trigger"
            focusable={isTitleTruncated}
          >
            <span ref={titleRef} className="model-title__text cgt-heading-50">{title}</span>
          </DesktopOnlyTooltip>
          {!isReadOnly && (
            <Button
              customClass="model-title__edit-button"
              type="secondary"
              size="tiny"
              variant="only-icon"
              aria-label="Edit model title"
              onClick={handleStartEditing}
              icon={<Icon svgComponent={EditOutline} color="dark" size="small" notSpaced />}
            />
          )}
        </>
      )}
    </div>
  )
}

export default memo(ModelTitle)

